import * as THREE from 'three';

/**
 * 二维有理 Bezier 曲线求值器。
 *
 * 职责：
 * 1. 使用齐次 de Casteljau 算法计算任意次数有理 Bezier 曲线点；
 * 2. 使用齐次导数控制点和商法则计算曲线对局部参数的一阶、二阶导数；
 * 3. 为 Puppy CAD 内核 等价参数曲线适配器提供统一的 Bezier D0/D1/D2 求值。
 *
 * 本类不负责校验 CAD 业务对象、求根、参数域筛选或交点分类。
 */
export class RationalBezierCurveEvaluator2D {
    /**
     * 计算二维有理 Bezier 曲线点。
     *
     * @param controlPoints Bezier 控制点。
     * @param weights 与控制点一一对应的正权重。
     * @param parameter 局部 `[0,1]` 参数。
     * @param target 结果写入目标。
     * @returns 写入后的二维曲线点。
     */
    public evaluatePoint(
        controlPoints: readonly THREE.Vector2[],
        weights: readonly number[],
        parameter: number,
        target: THREE.Vector2 = new THREE.Vector2(),
    ): THREE.Vector2 {
        const homogeneous = controlPoints.map((point, index) => {
            const weight = weights[index]!;
            return [point.x * weight, point.y * weight, weight];
        });
        const evaluated = this.evaluateHomogeneous(homogeneous, parameter);
        if (Math.abs(evaluated[2]) <= Number.EPSILON) {
            return target.set(Number.NaN, Number.NaN);
        }
        return target.set(evaluated[0] / evaluated[2], evaluated[1] / evaluated[2]);
    }

    /**
     * 计算二维有理 Bezier 曲线对局部参数的一阶导数。
     *
     * @param controlPoints Bezier 控制点。
     * @param weights 与控制点一一对应的正权重。
     * @param parameter 局部 `[0,1]` 参数。
     * @param target 结果写入目标。
     * @returns 写入后的二维导数向量。
     */
    public evaluateDerivative(
        controlPoints: readonly THREE.Vector2[],
        weights: readonly number[],
        parameter: number,
        target: THREE.Vector2 = new THREE.Vector2(),
    ): THREE.Vector2 {
        const degree = controlPoints.length - 1;
        if (degree <= 0) {
            return target.set(0, 0);
        }
        const homogeneous = controlPoints.map((point, index) => {
            const weight = weights[index]!;
            return [point.x * weight, point.y * weight, weight];
        });
        const derivativeControlPoints = homogeneous.slice(0, -1).map((point, index) => ([
            degree * (homogeneous[index + 1]![0] - point[0]),
            degree * (homogeneous[index + 1]![1] - point[1]),
            degree * (homogeneous[index + 1]![2] - point[2]),
        ]));
        const value = this.evaluateHomogeneous(homogeneous, parameter);
        const derivative = this.evaluateHomogeneous(derivativeControlPoints, parameter);
        const denominator = value[2] * value[2];
        if (Math.abs(denominator) <= Number.EPSILON) {
            return target.set(0, 0);
        }
        return target.set(
            (derivative[0] * value[2] - value[0] * derivative[2]) / denominator,
            (derivative[1] * value[2] - value[1] * derivative[2]) / denominator,
        );
    }

    /**
     * 计算二维有理 Bezier 曲线对局部参数的二阶导数。
     *
     * Puppy CAD 内核 transition 判定会在一阶导退化时使用二阶导，因此不能用显示折线或固定差分代替。
     * 这里在齐次坐标中分别求 `H`、`H'`、`H''`，再按有理商函数二阶导公式恢复。
     *
     * @param controlPoints Bezier 控制点。
     * @param weights 与控制点一一对应的正权重。
     * @param parameter 局部 `[0,1]` 参数。
     * @param target 结果写入目标。
     * @returns 写入后的二维二阶导数。
     */
    public evaluateSecondDerivative(
        controlPoints: readonly THREE.Vector2[],
        weights: readonly number[],
        parameter: number,
        target: THREE.Vector2 = new THREE.Vector2(),
    ): THREE.Vector2 {
        const degree = controlPoints.length - 1;
        if (degree <= 1) {
            return target.set(0, 0);
        }
        const homogeneous = controlPoints.map((point, index) => {
            const weight = weights[index]!;
            return [point.x * weight, point.y * weight, weight];
        });
        const firstDerivativeControlPoints = homogeneous.slice(0, -1).map((point, index) => ([
            degree * (homogeneous[index + 1]![0] - point[0]),
            degree * (homogeneous[index + 1]![1] - point[1]),
            degree * (homogeneous[index + 1]![2] - point[2]),
        ]));
        const secondDerivativeControlPoints = firstDerivativeControlPoints.slice(0, -1)
            .map((point, index) => ([
                (degree - 1) * (firstDerivativeControlPoints[index + 1]![0] - point[0]),
                (degree - 1) * (firstDerivativeControlPoints[index + 1]![1] - point[1]),
                (degree - 1) * (firstDerivativeControlPoints[index + 1]![2] - point[2]),
            ]));
        const value = this.evaluateHomogeneous(homogeneous, parameter);
        const firstDerivative = this.evaluateHomogeneous(firstDerivativeControlPoints, parameter);
        const secondDerivative = this.evaluateHomogeneous(secondDerivativeControlPoints, parameter);
        const weight = value[2];
        if (Math.abs(weight) <= Number.EPSILON) {
            return target.set(0, 0);
        }

        const weightSquared = weight * weight;
        const weightCubed = weightSquared * weight;
        return target.set(
            secondDerivative[0] / weight
                - value[0] * secondDerivative[2] / weightSquared
                - 2 * firstDerivative[0] * firstDerivative[2] / weightSquared
                + 2 * value[0] * firstDerivative[2] * firstDerivative[2] / weightCubed,
            secondDerivative[1] / weight
                - value[1] * secondDerivative[2] / weightSquared
                - 2 * firstDerivative[1] * firstDerivative[2] / weightSquared
                + 2 * value[1] * firstDerivative[2] * firstDerivative[2] / weightCubed,
        );
    }

    /**
     * 使用 de Casteljau 算法计算三分量齐次 Bezier 值。
     *
     * @param controlPoints 齐次控制点。
     * @param parameter 局部 `[0,1]` 参数。
     * @returns 齐次坐标三元组。
     */
    private evaluateHomogeneous(
        controlPoints: readonly number[][],
        parameter: number,
    ): [number, number, number] {
        const working = controlPoints.map((point) => [point[0]!, point[1]!, point[2]!]);
        for (let level = 1; level < working.length; level += 1) {
            const remaining = working.length - level;
            for (let index = 0; index < remaining; index += 1) {
                working[index]![0] = (1 - parameter) * working[index]![0]
                    + parameter * working[index + 1]![0];
                working[index]![1] = (1 - parameter) * working[index]![1]
                    + parameter * working[index + 1]![1];
                working[index]![2] = (1 - parameter) * working[index]![2]
                    + parameter * working[index + 1]![2];
            }
        }
        return working[0]! as [number, number, number];
    }
}
